import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IGoodsTransfer } from "../../../../types/Inventory/GoodsTransfer";
import { formatDate } from "../../../../utils/Date";
import { dateFilterParams } from "../../../../utils/Formatter/AgGridFilter";
import { statusValueFormatter } from "../../../../utils/Formatter/Global";
import CustomizedAvatar from "../../../Custom/CustomizedAvatar";
import CustomizedLetterAvatar from "../../../Custom/CustomizedLetterAvatar";
import CustomizedStatus from "../../../Custom/CustomizedStatus";

export const useGoodsTransferColumnDefs = () => {
  const { t } = useTranslation();
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();

  useEffect(() => {
    setColumnDefs([
      {
        field: "created_date",
        headerName: t("inventory.goods_transfer.table.column.created_date"),
        sort: "desc",
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "posted_date",
        headerName: t("inventory.goods_transfer.table.column.posted_date"),
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "unique_id",
        headerName: t("inventory.goods_transfer.table.column.unique_id"),
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "source_branch_id",
        filter: "agNumberColumnFilter",
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        field: "source_branch_name",
        headerName: t("inventory.goods_transfer.table.column.source_branch"),
        filter: "agTextColumnFilter",
      },
      {
        field: "source_warehouse_name",
        headerName: t("inventory.goods_transfer.table.column.source_warehouse"),
        filter: "agTextColumnFilter",
      },
      {
        field: "destination_branch_name",
        headerName: t(
          "inventory.goods_transfer.table.column.destination_branch"
        ),
        filter: "agTextColumnFilter",
      },
      {
        field: "destination_warehouse_name",
        headerName: t(
          "inventory.goods_transfer.table.column.destination_warehouse"
        ),
        filter: "agTextColumnFilter",
      },
      {
        field: "created_by",
        headerName: t("inventory.goods_transfer.table.column.created_by"),
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, string>) => {
          if (params.value) {
            if (params.data?.img_url && params.data?.img_url.length > 0) {
              return (
                <CustomizedAvatar
                  avatars={[
                    {
                      unique_id: "",
                      first_name: params.value,
                      last_name: "",
                      img_url: params.data.img_url,
                    },
                  ]}
                />
              );
            } else {
              return <CustomizedLetterAvatar name={params.value ?? ""} />;
            }
          }
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "remark",
        headerName: t("inventory.goods_transfer.table.column.remark"),
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "aggrid_status",
        headerName: t("inventory.goods_transfer.table.column.status"),
        filter: "agSetColumnFilter",
        cellRenderer: (params: ICellRendererParams<IGoodsTransfer, string>) => (
          <CustomizedStatus status={params.value} />
        ),
        filterParams: {
          values: [
            "draft",
            "wait_approve",
            "not_approved",
            "wait_transfer",
            "in_transit",
            "finished",
            "cancelled",
          ],
          valueFormatter: (params: ValueFormatterParams) =>
            statusValueFormatter(params.value),
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    ]);
  }, [t]);

  return columnDefs;
};
